/* @ngInject */

export default (
  $rootScope,
  helperService,
  helperPdfService,
  assetFinanceService
) => {
  /**
   * Prepare asset commitment schedule details.
   *
   * @param {Array} assetCommitments
   * @param {Array} lenders
   * @param {Object} contact
   * @param {String} pageBreak
   * @param {Boolean} isPageBreak
   *
   * @returns {Array}
   */
  const prepareAssetCommitmentScheduleDetails = ({
    assetCommitments,
    lenders,
    contact,
    isPageBreak
  }) => {
    const content = [];
    if (assetCommitments?.length) {
      if (isPageBreak) {
        content.push({
          text: '',
          margin: [0, 10, 0, 0]
        });
      }
      let isPB = isPageBreak;
      const aLen = assetCommitments.length;
      for (let a = 0; a < aLen; a += 1) {
        const assetCommitment = assetCommitments[a];
        if (!isPB) {
          content.push({
            text: '',
            pageBreak: 'before'
          });
          isPB = true;
        }
        if (a === 0) {
          content.push(
            helperPdfService.createHeader({
              title: `ASSET COMMITMENT SCHEDULE - ${contact.getName().toUpperCase()}`
            })
          );
        }
        content.push(
          helperPdfService.createSubHeader({
            title: `ASSET COMMITMENT SCHEDULE #${a + 1}`
          })
        );
        content.push({
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [
              [
                {
                  text: [
                    { text: 'Asset\n', style: 'label' },
                    {
                      text: assetCommitment?.asset || '-',
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Lender\n', style: 'label' },
                    {
                      text: helperService.getLenderByContentType({
                        lenders,
                        lenderAbbr: assetCommitment?.lender,
                        lenderOtherName: assetCommitment?.lenderOtherName,
                        contentType: 'abbr'
                      }),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    {
                      text: 'Date taken out\n',
                      style: 'label'
                    },
                    {
                      text: assetCommitment.getDateTakenOut(
                        $rootScope.CurrentTimeZone(),
                        $rootScope.CurrentLocale()
                      ),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Net amount financed\n', style: 'label' },
                    {
                      text: helperService
                        .isNumber(assetCommitment?.netAmountFinanced, true)
                        .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                      style: 'value'
                    }
                  ]
                }
              ],
              [
                {
                  text: [
                    { text: 'Facility\n', style: 'label' },
                    {
                      text: helperService.getAssetFinanceProductType(assetCommitment?.facility),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Rate\n', style: 'label' },
                    {
                      text: `${assetCommitment?.rate.toFixed(2)}%`,
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Term\n', style: 'label' },
                    {
                      text: helperService.getTitleOfSelectedTermInMonths({
                        term: assetCommitment?.term
                      }),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Repayments\n', style: 'label' },
                    {
                      text: helperService
                        .isNumber(assetCommitment?.repayments, true)
                        .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                      style: 'value'
                    }
                  ]
                }
              ],
              [
                {
                  text: [
                    { text: 'Current market value\n', style: 'label' },
                    {
                      text: helperService
                        .isNumber(assetCommitment?.currentMarketValue, true)
                        .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Outstanding\n', style: 'label' },
                    {
                      text: helperService
                        .isNumber(assetCommitment?.outstanding, true)
                        .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Residual value/balloon\n', style: 'label' },
                    {
                      text: helperService
                        .isNumber(assetCommitment?.residualValueBalloon, true)
                        .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Account number\n', style: 'label' },
                    {
                      text: assetCommitment?.accountNumber || '-',
                      style: 'value'
                    }
                  ]
                }
              ],
              [
                {
                  text: [
                    { text: 'To be paid out\n', style: 'label' },
                    {
                      text: assetCommitment?.toBePaidOut ? 'Yes' : 'No',
                      style: 'value'
                    }
                  ]
                },
                '',
                '',
                ''
              ]
            ]
          },
          layout: 'noBorders'
        });
      }
      content.push(helperPdfService.createSubHeader({ title: 'TOTAL' }));
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              {
                text: [
                  { text: 'Total repayments\n', style: 'label' },
                  {
                    text: assetFinanceService
                      .getAssetCommitmentScheduleTotal({
                        assetCommitments,
                        field: 'repayments'
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Total current market value\n', style: 'label' },
                  {
                    text: assetFinanceService
                      .getAssetCommitmentScheduleTotal({
                        assetCommitments,
                        field: 'currentMarketValue'
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Total outstanding\n', style: 'label' },
                  {
                    text: assetFinanceService
                      .getAssetCommitmentScheduleTotal({
                        assetCommitments,
                        field: 'outstanding'
                      })
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ]
              },
              ''
            ]
          ]
        },
        layout: 'noBorders'
      });
    }
    return content;
  };

  /**
   * Prepare asset commitment schedules.
   *
   * @param {Array} assetCommitmentSchedules
   * @param {Array} contacts
   * @param {Array} lenders
   *
   * @returns {Array}
   */
  const prepareAssetCommitmentSchedules = ({ assetCommitmentSchedules, contacts, lenders }) => {
    const content = [];
    const companies = _.filter(contacts, (c) => c.isCompany);
    if (assetCommitmentSchedules?.length && companies.length) {
      const cLen = companies.length;
      for (let c = 0; c < cLen; c += 1) {
        const contact = companies[c];
        const contactAssetCommitments = _.find(
          assetCommitmentSchedules,
          (a) => a.idContact === contact.id
        );
        content.push(
          prepareAssetCommitmentScheduleDetails({
            assetCommitments: contactAssetCommitments?.assetCommitments,
            lenders,
            contact,
            isPageBreak: false
          })
        );
      }
    }
    return content;
  };

  return {
    prepareAssetCommitmentScheduleDetails,
    prepareAssetCommitmentSchedules
  };
};
